import {
    Alert,
    Anchor,
    Card,
    Group,
    Stack,
    Switch,
    Text,
    Tooltip,
} from '@mantine/core';
import {AiOutlineInfoCircle} from 'react-icons/ai';

import {
    ControlReadiness,
    ControlState,
    PlatformControl,
    RestingState,
} from '../types';
import {
    ControlStateBadge,
    ReadinessBadge,
} from '../profile/Badges';

const JIRA_BROWSE_URL = 'https://flexinetwork.atlassian.net/browse';

interface ControlCardProps {
    control: PlatformControl;
    /** Omitted for NOT_WIRED controls — there is nothing to toggle. */
    onToggle?: (next: ControlState) => void;
    /** True while a flip is in flight, so the switch cannot be double-fired. */
    busy?: boolean;
    /** False when the signed-in user may not operate controls. */
    canOperate?: boolean;
}

const formatLastChanged = (control: PlatformControl) => {
    if (!control.lastChangedAt) {
        return null;
    }

    const when = new Date(control.lastChangedAt).toLocaleString();

    return control.lastChangedBy
           ? `Last changed by ${control.lastChangedBy} on ${when}`
           : `Last changed ${when}`;
};

export const ControlCard = ({
                                control,
                                onToggle,
                                busy = false,
                                canOperate = true,
                            }: ControlCardProps) => {

    const operational = control.readiness === ControlReadiness.OPERATIONAL;
    const lastChanged = formatLastChanged(control);

    // A control sitting away from its resting position is the thing an operator
    // most needs to notice — someone is deliberately holding it there.
    const heldOff = operational
        && control.state !== ControlState.UNKNOWN
        && control.state !== (control.restingState === RestingState.ON ? ControlState.ON : ControlState.OFF);

    const toggleDisabled = !operational || busy || !canOperate;

    const disabledReason = !operational
                           ? 'This control cannot be operated from here yet.'
                           : !canOperate
                             ? 'You do not have permission to change platform controls.'
                             : undefined;

    const toggle = (
        <Switch
            size={'lg'}
            checked={control.state === ControlState.ON}
            disabled={toggleDisabled}
            aria-label={`Turn ${control.name} ${control.state === ControlState.ON ? 'off' : 'on'}`}
            onChange={() => onToggle?.(
                control.state === ControlState.ON ? ControlState.OFF : ControlState.ON,
            )}
        />
    );

    return (
        <Card withBorder radius={'md'} p={'lg'} opacity={operational ? 1 : 0.75}>
            <Stack gap={'xs'}>

                <Group justify={'space-between'} align={'flex-start'} wrap={'nowrap'}>
                    <Stack gap={4}>
                        <Group gap={'xs'}>
                            <Text fw={600} size={'lg'}>{control.name}</Text>
                            <ReadinessBadge readiness={control.readiness}/>
                            {operational && <ControlStateBadge state={control.state}/>}
                        </Group>
                        <Text size={'sm'} c={'dimmed'}>{control.description}</Text>
                    </Stack>

                    {disabledReason
                     ? <Tooltip label={disabledReason} withArrow multiline w={260}>
                         <div>{toggle}</div>
                     </Tooltip>
                     : toggle}
                </Group>

                <Text size={'xs'} c={'dimmed'}>
                    Enforced at: {control.enforcedAt}
                </Text>

                {operational && typeof control.flipLatencySeconds === 'number' && (
                    <Text size={'xs'} c={'dimmed'}>
                        Takes effect within {control.flipLatencySeconds} seconds of a change.
                    </Text>
                )}

                {lastChanged && (
                    <Text size={'xs'} c={'dimmed'}>{lastChanged}</Text>
                )}

                {heldOff && (
                    <Alert
                        color={'orange'}
                        variant={'light'}
                        icon={<AiOutlineInfoCircle/>}
                        title={'Held away from its normal position'}
                    >
                        This control normally sits {control.restingState === RestingState.ON ? 'on' : 'off'}.
                        Someone changed it deliberately — turn it back when the reason has passed.
                    </Alert>
                )}

                {!operational && control.notWiredReason && (
                    <Alert color={'yellow'} variant={'light'} icon={<AiOutlineInfoCircle/>}>
                        <Text size={'sm'}>{control.notWiredReason}</Text>
                        {control.owningTicket && (
                            <Text size={'sm'} mt={'xs'}>
                                Tracked in{' '}
                                <Anchor
                                    href={`${JIRA_BROWSE_URL}/${control.owningTicket}`}
                                    target={'_blank'}
                                    rel={'noreferrer'}
                                >
                                    {control.owningTicket}
                                </Anchor>
                                .
                            </Text>
                        )}
                    </Alert>
                )}

            </Stack>
        </Card>
    );
};
